import * as React from 'react';

import { useClickTracker } from '@shared-ui/clickstream-analytics-context';
import { useLocalization } from '@shared-ui/localization-context';

import { usePropertyListing } from 'src/common/stores/contexts';

import { UitkCardLink } from 'uitk-react-cards';

import { hotelProductsSchema, multiItemProductsSchema } from 'utils/uis-prime-micro-messages';
import { updateSlotsParamForMapTravelAds } from 'utils/infosite-url-utils';

import { DoubleLink } from 'components/common/media/double-link';
import { PropertyFields } from 'src/common/__generated__/api';
import { TargetableLink } from 'components/common/targetable-link';
import { UserSetListingComponentProps } from 'components/search/listing';

export interface CardLinkProps extends UserSetListingComponentProps {
  listingClickHandler: (property: PropertyFields.Fragment) => void;
}

export const CardLink: React.FunctionComponent<CardLinkProps> = ({ listingClickHandler, referralObject, multiItem, mapListingView }) => {
  const property = usePropertyListing();
  const track = useClickTracker();

  const { dataAttributes, name, propertyDetailsLink, sponsoredListing } = property;

  if (!propertyDetailsLink?.uri) {
    return null;
  }

  let url = sponsoredListing && multiItem?.multiItemContext ? propertyDetailsLink.uri.value : propertyDetailsLink.uri.relativePath;

  if (mapListingView && sponsoredListing) {
    url = updateSlotsParamForMapTravelAds(url);
  }

  const listingDataAttributes = {};
  if (dataAttributes) {
    listingDataAttributes[dataAttributes.key] = dataAttributes.value;
  }

  const onClick = () => {
    if (listingClickHandler) {
      listingClickHandler(property);
    }
    const packageType = multiItem?.getPackageType();
    const trackMethod = packageType ? multiItemProductsSchema : hotelProductsSchema;
    const referrerId = propertyDetailsLink?.clientSideAnalytics?.referrerId ? propertyDetailsLink.clientSideAnalytics.referrerId : '';
    const linkName = propertyDetailsLink?.clientSideAnalytics?.linkName ? propertyDetailsLink.clientSideAnalytics.linkName : '';

    track(referralObject?.navigation?.rfrr || referrerId, linkName, true, trackMethod(property, packageType));
  };

  const doubleLinkProps = {
    name,
    className: 'listing__link',
    'data-stid': 'open-hotel-information',
    to: url,
    onClick,
    ...listingDataAttributes,
    isTravelAd: Boolean(sponsoredListing?.clickRedirectLinkoffTrackingUrl),
  };

  if (doubleLinkProps.isTravelAd) {
    return <CardLinkBase {...doubleLinkProps} />;
  }

  return (
    <TargetableLink>
      <CardLinkBase {...doubleLinkProps} />
    </TargetableLink>
  );
};
CardLink.displayName = 'CardLink';

const CardLinkBase = ({ name, isTravelAd, ...doubleLinkProps }) => {
  const l10n = useLocalization();

  const travelAdA11yLabel = l10n.formatText('hotels.search.listing.allyLabelTravelAd', [name]);
  const listingA11yLabel = l10n.formatText('hotels.search.listing.a11yLabel', [name]);
  const newWindowMessage = l10n.formatText('hotels.infosite.policies.missingIncorrectInfo.newWindowText');

  return (
    <UitkCardLink data-stid="listing_link">
      <DoubleLink {...doubleLinkProps}>{isTravelAd ? travelAdA11yLabel : `${listingA11yLabel}, ${newWindowMessage}`}</DoubleLink>
    </UitkCardLink>
  );
};

CardLinkBase.displayName = 'CardLinkBase';
